import { useEffect, useMemo } from 'react';
import { useLocation, useRouteMatch } from 'react-router-dom';
import { CREATE_PLAYLIST_ADD_LESSON_PARAM, CREATE_PLAYLIST_ADD_SHORT_PARAM, CREATE_PLAYLIST_ADD_TRACK_PARAM, CREATE_PLAYLIST_ITEM_LANGUAGE_PARAM, MY_SAVED_ITEMS_PAGE_SIZE } from './savedItemsTypes';
import { useLatestSavedItemsInteractions, usePlaylist } from './savedItemsQueries';
import { CREATE_PLAYLIST_URL_FRAGMENT, EDIT_PLAYLIST_URL_FRAGMENT, PLAYLIST_RESULTS_URL_FRAGMENT, PLAYLIST_URL_FRAGMENT, MY_PLAYLISTS_URL_FRAGMENT, DUPLICATE_PLAYLIST_URL_FRAGMENT } from '../../constants/Navigation';
import { FILTER_CONSTS } from '../../constants/TopFilterConstants';
import { removeTrailingSlash } from '../../utils/RoutingUtils';
import { useMediaQuery } from 'academy-common-lib/utils/Styling';
import { usePlaylistProgress } from '../userProgress/userProgressQueries';
import { useMyPlaylists } from '../myPlaylists/myPlaylistsQueries';
import { useHasGate } from '../accounts';
import { useHasScope } from '../accounts/accountsQueries';
import { useUserOwnsPlaylist } from './savedItemsQueries';
import { useCatalogCardModels } from 'academy-common-lib/features/catalog/hooks/useCatalogCardModels';
import { useSimplePlaylists } from 'academy-common-lib/features/hubspotPlaylists/hooks/useSimplePlaylists';
import getLang from 'academy-common-lib/utils/getLang';
export const useCloseCreatePlaylistModalPath = () => {
  const location = useLocation();
  return useMemo(() => {
    const {
      pathname: currentPathname
    } = location;
    const createPlaylistUrlFragmentIndex = currentPathname.indexOf(CREATE_PLAYLIST_URL_FRAGMENT);
    const editPlaylistUrlFragmentIndex = currentPathname.indexOf(EDIT_PLAYLIST_URL_FRAGMENT);
    const duplicatePlaylistUrlFragmentIndex = currentPathname.indexOf(DUPLICATE_PLAYLIST_URL_FRAGMENT);
    let originalUrl = currentPathname;
    if (createPlaylistUrlFragmentIndex !== -1) {
      originalUrl = currentPathname.substring(0, createPlaylistUrlFragmentIndex);
    } else if (editPlaylistUrlFragmentIndex !== -1) {
      originalUrl = currentPathname.substring(0, editPlaylistUrlFragmentIndex);
    } else if (duplicatePlaylistUrlFragmentIndex !== -1) {
      if (location.state && location.state.from) {
        originalUrl = location.state.from.pathname;
      } else if (originalUrl === null) {
        originalUrl = PLAYLIST_URL_FRAGMENT;
      }
    }
    const originalParams = new URLSearchParams(location.search);
    originalParams.delete(CREATE_PLAYLIST_ADD_TRACK_PARAM);
    originalParams.delete(CREATE_PLAYLIST_ADD_LESSON_PARAM);
    originalParams.delete(CREATE_PLAYLIST_ADD_SHORT_PARAM);
    originalParams.delete(CREATE_PLAYLIST_ITEM_LANGUAGE_PARAM);
    return `${removeTrailingSlash(originalUrl) || '/'}${originalParams.size ? `?${originalParams}` : ''}`;
  }, [location]);
};
export const useCreatePlaylistModalPath = item => {
  const location = useLocation();
  return useMemo(() => {
    let sourceUrl = removeTrailingSlash(location.pathname);
    if (sourceUrl === '/') sourceUrl = '';
    const params = new URLSearchParams(location.search);
    if (item) {
      const {
        type,
        id,
        language
      } = item;
      if (type === 'TRACK') params.append(CREATE_PLAYLIST_ADD_TRACK_PARAM, id.toString());
      if (type === 'LESSON') params.append(CREATE_PLAYLIST_ADD_LESSON_PARAM, id.toString());
      if (type === 'SHORT_FORM_CONTENT') params.append(CREATE_PLAYLIST_ADD_SHORT_PARAM, id.toString());
      if (language) params.append(CREATE_PLAYLIST_ITEM_LANGUAGE_PARAM, language);
    }
    return `${sourceUrl}${CREATE_PLAYLIST_URL_FRAGMENT}${params.size ? `?${params}` : ''}`;
  }, [item, location.pathname, location.search]);
};
export const useSavedItemsTooltip = () => {
  var _catalogCardModels$fi, _simplePlaylists;
  const {
    latestInteraction,
    removeLatestInteraction
  } = useLatestSavedItemsInteractions();
  const {
    itemId,
    playlistId,
    type,
    action,
    language,
    portalId,
    assignmentIds
  } = latestInteraction || {};
  const {
    catalogCardModels,
    catalogCardModelsLoading
  } = useCatalogCardModels({
    items: [{
      id: itemId,
      type: type,
      language: language || getLang()
    }],
    skip: !itemId || !type
  });
  const {
    simplePlaylists,
    simplePlaylistsLoading
  } = useSimplePlaylists({
    playlistIds: [playlistId],
    skip: !playlistId
  });
  const itemName = catalogCardModels === null || catalogCardModels === void 0 || (_catalogCardModels$fi = catalogCardModels.find(model => model.id === itemId)) === null || _catalogCardModels$fi === void 0 ? void 0 : _catalogCardModels$fi.name;
  const show = !!latestInteraction && !catalogCardModelsLoading && !simplePlaylistsLoading;
  const playlistName = simplePlaylists === null || simplePlaylists === void 0 || (_simplePlaylists = simplePlaylists[playlistId]) === null || _simplePlaylists === void 0 ? void 0 : _simplePlaylists.name;
  return {
    show,
    removeLatestInteraction,
    action,
    itemName,
    itemId,
    itemType: type,
    playlistName,
    playlistId,
    portalId,
    assignmentIds
  };
};
export const useShowNavbarWatchLaterTooltip = () => {
  const {
    show,
    action
  } = useSavedItemsTooltip();
  return show && action && ['ADD_TO_WATCH_LATER', 'REMOVE_FROM_WATCH_LATER'].includes(action);
};
export const useShowNavbarPlaylistsTooltip = () => {
  const {
    show,
    action
  } = useSavedItemsTooltip();
  return show && action && ['SAVE_PLAYLIST', 'UNSAVE_PLAYLIST', 'ARCHIVE_PLAYLIST', 'UNARCHIVE_PLAYLIST', 'DUPLICATE_PLAYLIST', 'ADD_TO_PLAYLIST', 'REMOVE_FROM_PLAYLIST', 'CREATE_PLAYLIST_ASSIGNMENT'].includes(action);
};
export const useTimedLatestSavedItemsInteraction = bypass => {
  const {
    isMobile
  } = useMediaQuery();
  const {
    latestInteraction,
    removeLatestInteraction
  } = useLatestSavedItemsInteractions();
  useEffect(() => {
    if (isMobile || latestInteraction && !bypass) {
      const timeout = setTimeout(() => {
        removeLatestInteraction();
      }, 5000);
      return () => clearTimeout(timeout);
    }
    return undefined;
  }, [latestInteraction, removeLatestInteraction, isMobile, bypass]);
  return latestInteraction ? latestInteraction.action : null;
};
export const useAddToPlaylistOptionList = ({
  page = 1,
  skip = false
}) => useMyPlaylists({
  tab: 'created-by-me',
  limit: MY_SAVED_ITEMS_PAGE_SIZE,
  page,
  skip
});
export const usePlaylistRemainingItemsAndProgress = playlistId => {
  const {
    playlistProgress,
    playlistProgressLoading
  } = usePlaylistProgress(playlistId);
  const status = playlistProgress ? playlistProgress.status : null;
  const remainingProgressItems = playlistProgress ? playlistProgress.itemsProgress.filter(item => item.status !== 'COMPLETED') : [];
  const remainingProgressItemCount = remainingProgressItems.length;
  const totalProgressItemCount = playlistProgress ? playlistProgress.itemsProgress.length : 0;
  const progressAmount = status === FILTER_CONSTS.STATUSES.COMPLETED ? 1 : status === FILTER_CONSTS.STATUSES.IN_PROGRESS ? (totalProgressItemCount - remainingProgressItemCount) / totalProgressItemCount : 0;
  return {
    remainingProgressItems,
    remainingProgressItemCount,
    totalProgressItemCount,
    progressAmount,
    status,
    playlistProgressLoading
  };
};
export const useIsPlaylistRoute = () => {
  const match = useRouteMatch([PLAYLIST_RESULTS_URL_FRAGMENT, PLAYLIST_URL_FRAGMENT, MY_PLAYLISTS_URL_FRAGMENT]);
  return match ? match.isExact : false;
};

// This hook can be used with a playlist object or a playlistId
export const useIsHubspotPlaylist = playlistOrPlaylistId => {
  const isPlaylist = playlistOrPlaylistId && typeof playlistOrPlaylistId === 'object';
  const playlistId = isPlaylist ? 'id' in playlistOrPlaylistId ? playlistOrPlaylistId.id :
  // @ts-expect-error we're in the process of removing this
  playlistOrPlaylistId.playlistId : playlistOrPlaylistId;
  const {
    hasGate: isUngatedToHubspotPlaylists
  } = useHasGate('Academy:LearningPaths');
  const {
    playlist: playlistFromQuery
  } = usePlaylist(playlistId, !!(!playlistId || isPlaylist));
  const playlist = isPlaylist ? playlistOrPlaylistId : playlistFromQuery;
  const isAHubspotPlaylist = (playlist === null || playlist === void 0 ? void 0 : playlist.type) === 'LEARNING_PATH';
  return isAHubspotPlaylist && isUngatedToHubspotPlaylists;
};
export const useCanUserEditPlaylist = playlistId => {
  const {
    playlist
  } = usePlaylist(playlistId, !playlistId);
  const userIsOwner = useUserOwnsPlaylist(playlist === null || playlist === void 0 ? void 0 : playlist.id);
  const isHubspotPlaylist = useIsHubspotPlaylist(playlistId);
  const {
    hasScope: hasPublicPlaylistsAdminScope
  } = useHasScope('academy-public-playlists-admin');
  if (isHubspotPlaylist && hasPublicPlaylistsAdminScope) return true;
  return userIsOwner;
};